'use client';
import { ResponseStoreFrontSmeLoan } from '@/src/interfaces/data-contracts';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import fmt from '@/src/utils/helpers/number-format';
import { formatInterestRate } from '@/src/utils/helpers/replace-dot-with-comma';
import classnames from 'classnames';
import Link from 'next/link';
import React, { useState } from 'react';
import LinkLikeButton from '../../shared/link-like-button';
import ImageWithFallback from '../../ui/image/image-with-fallback';
import { RelLink } from '../../ui/rel-link';
import { Container } from './container';
import QRComponent from '../qr-component';
import { Button } from '../../ui/button/button';
import { useRouter } from 'next/navigation';
import Modal from '../../ui/modal/modal';
import { CustomerStatusModal } from '../../shared/modal-content/customer-status-modal';
import Tooltip from '../../ui/tooltip/tooltip';
import { InfoIcon } from '../../ui/icons/icons';

type Props = { item: ResponseStoreFrontSmeLoan; hasAgreement: boolean; isMobile: boolean };

export const QueryItem: React.FC<Props> = ({ item, hasAgreement = true, isMobile }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const router = useRouter();

  const handleApplyClick = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  // Bu kismin Fibabanka disindaki entegrasyonlar ile birlikte genellestirilmesi gerekiyor.
  const isFibabanka = item.bankId === 5;
  // @ts-ignore
  const isFormIntegration = item.recourseStatusTypeEnum === 1; // 1 = FORM
  const integrationLink = `/ticari-kredi/fibabanka/int/basvuru/form?ProductId=${item.productId}&Amount=${item.amount}&Maturity=${item.maturity}&Rate=${item.interestRate}`;
  const integrationInit = () => {
    router.push(integrationLink);
  };

  return (
    <Container
      item={item}
      className={classnames(' flex flex-col md:flex-row md:items-center', hasAgreement && 'md:justify-between')}
      data-testid="productCard"
    >
      {hasAgreement ? (
        <div
          className="md:after:border-r-black-200 flex w-full items-center md:after:inline-block md:after:h-10  md:after:border-r md:after:pl-8"
          data-testid="title-container"
        >
          <div
            className="sme-img mr-12 flex h-[66px] w-[122px] shrink-0 items-center justify-center rounded-xl border border-black300"
            data-testid="img-container"
          >
            {item.bank?.bankSeoUrl ? (
              <Link
                href={item.bank?.bankSeoUrl}
                className="flex h-full w-full items-center justify-center"
                data-testid="img-link"
              >
                <div className="relative h-5 w-[80px]" data-testid="inner-img-container">
                  <ImageWithFallback
                    src={item.bank?.logoPath}
                    alt={item.name + ' Kobi Kredisi'}
                    title={item.name}
                    fill
                  />
                </div>
              </Link>
            ) : (
              <div className="relative h-5 w-[80px]" data-testid="inner-img-container">
                <ImageWithFallback
                  src={item.bank?.logoPath}
                  alt={item.name + ' Kobi Kredisi'}
                  title={item.name}
                  fill
                  data-testid="bankLogo"
                />
              </div>
            )}
          </div>
          <div
            className="sme-name flex-1  text-right text-xs font-semibold text-black800 md:max-w-[200px] md:text-left md:text-sm"
            data-testid="title"
          >
            <RelLink href={`${item.detailLink}`} data-testid="detailLink">
              {item.name}
            </RelLink>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1 md:w-[374px] md:border-r" data-testid="product-container">
          <div
            className="text-xs font-bold leading-[18px] text-black800 md:text-sm md:leading-[22px]"
            data-testid="bankName"
          >
            {item.bank?.name}
          </div>
          <div
            className="text-xs font-semibold leading-[18px] text-black800 md:text-sm md:leading-[22px]"
            data-testid="name"
          >
            {item.name}
          </div>
        </div>
      )}
      <div
        className={classnames(
          'mt-4 flex flex-row justify-between md:mt-0 md:gap-5',
          !hasAgreement && 'md:justify-between'
        )}
        data-testid="info-container"
      >
        <div className="sme-rate ml-4 flex flex-col md:min-w-[80px] md:text-right" data-testid="rate">
          <div className="flex h-4 items-center justify-end">
            <span className="block text-xs text-black700" data-testid="title">
              {item.bank?.financeType === 2 ? 'Kâr Payı' : 'Faiz Oranı'}
            </span>
            <div className={`${item.isVariableInterest ? 'ml-1' : 'hidden'}`}>
              <Tooltip
                tooltipIcon={<InfoIcon width={16} height={16} className="cursor-pointer text-black600" />}
                tooltipClassname={classnames('min-w-[238px] py-[20px] px-[12px] text-xs leading-4 font-semibold')}
                // className="text-xs md:text-base"
              >
                {item.loanRateText ?? 'Faiz oranı şirketinize göre değişiklik gösterebilir.'}
              </Tooltip>
            </div>
          </div>
          <span
            className="text-base font-semibold text-black800"
            data-testid="value
          ls"
          >
            %{formatInterestRate(item.interestRate)}
          </span>
        </div>
        <div className="sme-inst flex flex-col text-right md:min-w-[124px]" data-testid="monthlyInstallment">
          <span className="text-xs text-black700" data-testid="title">
            Aylık Taksit
          </span>
          <span className="text-base font-semibold text-black800" data-testid="value">
            {fmt(item.monthlyInstallment, CurrencyTypeMissing.TRY)}
          </span>
        </div>
        <div className="sme-total flex flex-col text-right md:min-w-[124px]" data-testid="totalAmount">
          <span className="text-xs text-black700" data-testid="title">
            Toplam Ödeme
          </span>
          <span className="text-base font-semibold text-black800" data-testid="value">
            {fmt(item.totalAmount, CurrencyTypeMissing.TRY)}
          </span>
        </div>
      </div>
      <div
        className={classnames(
          'sme-buttons mt-6 flex flex-row justify-between gap-2 md:ml-9 md:mt-0',
          (!hasAgreement || !item.recourseLink) && 'w-[310px] flex-row-reverse'
        )}
        data-testid="button-container"
      >
        {item.detailLink && (
          <LinkLikeButton
            href={item.detailLink.replace('Amount', 'amount').replace('Maturity', 'maturity')}
            text={'İncele'}
            className={classnames('h-[48px] w-[106px] md:w-[120px]', !hasAgreement && 'w-full')}
            external
            data-testid="detailLink"
          />
        )}
        {isFibabanka && item.isActiveIntegration && isFormIntegration ? (
          <Button
            text={'Hemen Başvur'}
            className="h-[48px] w-[194px] rounded-[6px] text-[16px] md:w-[182px]"
            onClick={integrationInit}
            data-testid="applyButton"
          />
        ) : (
          item.recourseLink &&
          hasAgreement &&
          !item.isQrPopUpActive && (
            <LinkLikeButton
              target={item.target}
              href={item.recourseLink}
              text={'Hemen Başvur'}
              className="h-[48px] w-[194px] md:w-[182px]"
              data-testid="applyButton"
            />
          )
        )}
        {!isMobile && hasAgreement && item.isQrPopUpActive && !item.isQrCustomerPopUpActive && (
          <>
            <Button
              className="h-[48px] w-[194px] rounded-lg text-sm font-bold md:w-[182px] md:text-base"
              text="Hemen Başvur"
              onClick={openPopup}
              data-testid="applyButton"
            />
            <QRComponent closePopup={closePopup} isPopupOpen={isPopupOpen} product={item} />
          </>
        )}
        {hasAgreement && item.isQrPopUpActive && item.isQrCustomerPopUpActive && (
          <>
            <Modal
              show={showModal}
              onClose={handleCloseModal}
              customSize
              className="w-[calc(100%-32px)] rounded-xl px-4 py-5 md:w-[600px] md:px-10 md:py-10"
              data-testid="customer-modal"
            >
              <CustomerStatusModal item={item} />
            </Modal>
            <Button
              className="h-[48px] w-[194px] rounded-lg text-sm font-bold md:w-[182px] md:text-base"
              text="Hemen Başvur"
              onClick={handleApplyClick}
              data-testid="applyButton"
            />
          </>
        )}
      </div>
    </Container>
  );
};
